// scanExport.js — the Scan tab's export loop, living for the whole session (module level), NOT in
// the HOVTab component. Tabs are unmounted when you switch away; a loop owned by the component
// kept running but lost its progress, its failure list and its end-of-run bookkeeping, and on
// re-entry the tab believed nothing was running (so a second export could start on top).
//
// What lives here: the state (running / done / total / failed), the per-file loop, a log of the
// files that landed (so a tab that was away applies them when it comes back) and the outcome
// (what to select after the run). What does NOT: anything that needs the tab's React state —
// HOVTab subscribes via useScanExport() and applies `landed` / the outcome whenever it is mounted.
//
// State + hooks: scanExportStore.js. Also read by components/RenderBar.js (shows the export from Studio / Export) and by
// components/ScanExportDock.js (shows it from the other tabs).
import axios from 'axios';
import { setState, resetRun, addLanded, setOutcome } from './scanExportStore';

const EXPORT_TIMEOUT_MS = 8 * 60 * 1000;   // one video download during an export

export function exportError(cause, message) { const e = new Error(message); e.cause = cause; return e; }

function onBeforeUnload(e) { e.preventDefault(); e.returnValue = ''; }   // closing / reloading the window does stop it

async function fileExists(dir, name) {
  try { await dir.getFileHandle(name); return true; } catch (e) { return false; }
}
async function writeBlobFlat(dirHandle, filename, blob) {
  const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}
async function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 8000);
}

// Export download with a typed cause, so a failure says WHY (see hovStreamError on the backend).
async function fetchVideoBlob(api, file) {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), EXPORT_TIMEOUT_MS) : null;
  try {
    let resp;
    try { resp = await fetch(api + '/api/hov/stream/' + file.id + '?export=1', ctl ? { signal: ctl.signal } : undefined); }
    catch (e) { throw e && e.name === 'AbortError' ? exportError('timeout', 'No answer after ' + Math.round(EXPORT_TIMEOUT_MS / 60000) + ' min') : exportError('network', 'Could not reach Timbo’s server (' + ((e && e.message) || 'network error') + ')'); }
    if (!resp.ok) {
      let body = null; try { body = await resp.json(); } catch (e) {}
      throw exportError((body && body.cause) || 'drive_http', (body && body.message) || 'Server answered HTTP ' + resp.status);
    }
    try { return await resp.blob(); }
    catch (e) { throw e && e.name === 'AbortError' ? exportError('timeout', 'Download still running after ' + Math.round(EXPORT_TIMEOUT_MS / 60000) + ' min') : exportError('network', 'Connection dropped mid-download — the Drive stream was cut'); }
  } finally { if (timer) clearTimeout(timer); }
}

function postStatus(api, id, body) {
  return axios.post(api + '/api/hov/status/' + encodeURIComponent(id), body, { timeout: 15000 })
    .then(() => true)
    .catch(e => { console.warn('[hov] status write failed', id, (e && e.message) || e); return false; });
}

// batch: files sorted by the caller · root: directory handle or null (browser downloads) ·
// counters: numbering already read · prefixOf(file): filename prefix · api: backend base URL
export async function startScanExport({ api, batch, root, counters, prefixOf }) {
  resetRun();
  setState({ running: true, done: 0, total: batch.length, msg: '', failed: [], unseen: false });
  window.addEventListener('beforeunload', onBeforeUnload);
  const folders = {}, failed = [];
  let done = 0;
  const pad2 = n => String(n).padStart(2, '0');
  for (let i = 0; i < batch.length; i++) {
    const f = batch[i];
    const prefix = prefixOf(f);
    try {
      let dir = null;
      if (root) dir = folders[f.feature] || (folders[f.feature] = await root.getDirectoryHandle(f.feature, { create: true }));
      let n = (counters[prefix] || 0) + 1, fname = prefix + '_' + pad2(n) + '.mp4';
      // Never overwrite a file already on disk (e.g. exported before this index existed).
      while (dir && await fileExists(dir, fname)) { n++; fname = prefix + '_' + pad2(n) + '.mp4'; }
      counters[prefix] = n;
      setState({ msg: f.feature + '/' + fname });
      const blob = await fetchVideoBlob(api, f);
      try { if (dir) await writeBlobFlat(dir, fname, blob); else await triggerDownload(blob, fname); }
      catch (e) { throw exportError('disk', 'Could not write ' + fname + ' (' + ((e && e.message) || 'disk error') + ')'); }
      // Mark as soon as it lands — an interrupted export keeps everything already written.
      const ok = await postStatus(api, f.id, { exported: true, exported_name: fname, feature: f.feature, language: f.market || null });
      if (!ok) failed.push({ f, cause: 'mark', error: 'Saved as ' + fname + ', but not marked exported' });
      addLanded({ id: f.id, exportedAt: Date.now(), exportedName: fname });
      done++;
      setState(s => Object.assign({}, s, { done: s.done + 1 }));
    } catch (err) {
      console.warn('[hov] export failed for', f.id, (err && err.cause) || '', (err && err.message) || err);
      failed.push({ f, cause: (err && err.cause) || 'network', error: (err && err.message) || 'unknown error' });
    }
  }
  window.removeEventListener('beforeunload', onBeforeUnload);
  const summary = done + ' / ' + batch.length + ' exported' + (failed.length ? ' · ' + failed.length + ' failed' : '');
  // Keep only what can be retried selected, so a retry is one click. "mark" is already on disk and
  // "unsupported" will fail again — both stay listed, out of the selection.
  setOutcome({ failedIds: new Set(failed.filter(x => x.cause !== 'mark' && x.cause !== 'unsupported').map(x => x.f.id)) });
  setState({
    running: false, done, total: batch.length, msg: summary, unseen: true,
    failed: failed.map(x => ({ id: x.f.id, creator: x.f.creator, name: x.f.originalName, cause: x.cause, error: x.error })),
  });
}
